/**
 * CustomSection - Custom Content Display
 *
 * Renders custom HTML/Markdown content for flexible branding.
 * Content is sanitized before rendering — DOMPurify pass followed by
 * a post-sanitization iframe domain-allowlist filter (mirrors HtmlBlock).
 * Iframes whose src doesn't match an allowlisted host are removed before
 * render, preventing phishing-iframe injection via admin-authored content.
 */

import DOMPurify from "dompurify";
import { ALLOWED_IFRAME_HOSTS } from "./HtmlBlock";
import "./CustomSection.scss";

interface CustomSectionProps {
    content?: string;
}

export const CustomSection = ({ content }: CustomSectionProps) => {
    /*
     * Unconfigured = invisible (the anchor convention: never a heading over
     * nothing). The editor rail is where an operator learns a section is
     * empty — the live page just skips it.
     */
    if (!content) {
        return null;
    }

    // Sanitize HTML content
    const sanitizedContent = DOMPurify.sanitize(content, {
        ALLOWED_TAGS: [
            "h1", "h2", "h3", "h4", "h5", "h6",
            "p", "a", "ul", "ol", "li",
            "strong", "em", "b", "i",
            "img", "video", "iframe",
            "div", "span", "br", "hr",
            "table", "thead", "tbody", "tr", "th", "td",
        ],
        ALLOWED_ATTR: [
            "href", "target", "rel",
            "src", "alt", "width", "height",
            "class", "id", "style",
            "allowfullscreen", "frameborder",
        ],
    });

    // Post-sanitization: strip iframes whose src doesn't match the domain allowlist
    // (mirrors HtmlBlock — see ALLOWED_IFRAME_HOSTS). Prevents phishing/tracking
    // iframes from leaking through despite the DOMPurify pass allowing the iframe tag itself.
    const div = document.createElement("div");
    div.innerHTML = sanitizedContent;
    div.querySelectorAll("iframe").forEach((iframe) => {
        try {
            const url = new URL(iframe.getAttribute("src") || "");
            const hostname = url.hostname.toLowerCase();
            if (!ALLOWED_IFRAME_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`))) {
                iframe.remove();
            }
        } catch {
            iframe.remove();
        }
    });
    const finalContent = div.innerHTML;

    return (
        <section className="section custom-section">
            <div
                className="custom-section__content"
                dangerouslySetInnerHTML={{ __html: finalContent }}
            />
        </section>
    );
};
